import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { validationSchema } from '../form';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { SingleRewardPicker } from 'v2/components/form/singleRewardPicker/SingleRewardPicker';
import { Select } from 'v2/components/select/Select';
import { VideoCategory } from 'v2/constants';
import { VideoForm } from 'v2/types/VideoModel';
import { enumToSelect } from 'v2/utils';

interface VideoFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: VideoForm;
  isSubmitting: boolean;
  onSubmit: (values: VideoForm, resetForm: VoidFunction) => void;
  isEditing?: boolean;
}

export const VideoFormDialog: FC<VideoFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
  isEditing,
}) => {
  const formik = useFormik<VideoForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
  });

  const handleSelectChange = (field: keyof VideoForm) => (value: string) => {
    formik.setFieldValue(field, value);
  };

  const handleSelectImage = (fileId: string) => {
    formik.setFieldValue('previewId', fileId);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="videoForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <Select
            value={formik.values.category}
            options={enumToSelect(VideoCategory)}
            label="Category"
            onChange={handleSelectChange('category')}
            required
          />

          <TextField
            name="secondsToReward"
            value={formik.values.secondsToReward === 0 ? '' : formik.values.secondsToReward}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Minimum viewing time (seconds)"
            type="number"
            required
          />

          <TextField
            name="link"
            value={formik.values.link}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Link"
            required
          />

          <SingleRewardPicker
            rewardType={formik.values.rewardType}
            onRewardTypeChange={handleSelectChange('rewardType')}
            rewardValue={formik.values.rewardValue}
            onRewardValueChange={handleSelectChange('rewardValue')}
            disabled={isEditing}
            required
          />

          <FileSelector
            value={formik.values.previewId}
            onSelect={handleSelectImage}
            error={formik.errors.previewId}
            label="Preview"
            required
          />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="videoForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
